import { useState, useMemo, useCallback } from 'react'
import { PdfViewer } from '@/components/pdfRender/PdfViewer'
import { usePdfViewer } from '@/hooks/pdf/usePdfViewer'
import { Dropdown, Button, Link } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { PrintGuideModal } from './'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useLocation } from 'react-router-dom'
import DesignerGate from 'components/auth/DesignerGate'
import buildUrlWithParams from 'utils/global/buildUrlWithParams'
import { useTranslation } from 'react-i18next'
import { getItemDisplayName } from 'utils/global/getItemDisplayName'
import { STATUS_KEY, M3 } from '@messenger/consts'

export const CustomizeProductHeader = ({
  order,
  productInfo,
  themeInfo,
  selectedLayout: [selectedLayout] = ['', null],
  layout: [layout, setLayout] = ['', null],
  productStatus: [productStatus, setProductStatus] = ['', productInfo.status],
  pageSet,
  allLayouts,
  oldProduct,
  isLoading,
  getOrderProducts,
  pdfState,
  updatePdfState,
  applicationType,
}) => {
  const { t } = useTranslation()
  const [showPrintGuide, setShowPrintGuide] = useState(false)
  const location = useLocation()
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const memoizedGetOrderProducts = useCallback(getOrderProducts, [
    getOrderProducts,
  ])
  const isAdmin = useIsAdmin()
  const {
    handlers: pdfViewerHandlers,
    setters: pdfViewerSetters,
    state: pdfViewerState,
  } = usePdfViewer({
    order,
    productInfo,
    pageSet,
    selectedLayout,
    params,
    getOrderProducts: memoizedGetOrderProducts,
    productStatus,
    setProductStatus,
    pdfState,
    updatePdfState,
    applicationType,
  })

  const {
    handleClosePdfViewer,
    handleCustomizeClick,
    handleGeneratePdfClick,
    handleFinalizePdf,
    handleRevertFinalizationClick,
    handleDropdownProductStatusChange,
  } = pdfViewerHandlers

  const { setDisclaimerAcknowledged } = pdfViewerSetters

  const {
    isProof,
    canCustomize,
    canRevertFinalization,
    disclaimerAcknowledged,
    generating,
    generatingText,
    pendingCloseOperation,
    pdfGenerationProgress,
    pdfGenerationStatus,
    pdfUrl,
    previewCustomizeProductHeaderDropdownsDisabled,
    showPdfViewer,
    proofGalleryDetails,
  } = pdfViewerState

  const productStatusValues = [
    { id: STATUS_KEY.EDITING, name: t('productStatus.editing') },
    { id: STATUS_KEY.PROOF_SENT, name: t('productStatus.proofSent') },
    { id: STATUS_KEY.FINALIZED, name: t('productStatus.finalized') },
    { id: STATUS_KEY.CLOSED, name: t('productStatus.closed') },
  ]

  const customizeUrl = buildUrlWithParams(`/editor/${pageSet?.id}`, params)
  const isProductStatusClosed = productStatus === STATUS_KEY.CLOSED
  const showingRevertFinalizationText =
    generatingText === t('customizeProductHeader.revertFinalization')
  const showingFinalizingText = generatingText === t('pdf.finalizing')
  const regularUserShouldNotHaveAccessToFinalPdf =
    !isAdmin && (showingRevertFinalizationText || showingFinalizingText)

  const disableDropdownButtons =
    isLoading ||
    generating ||
    previewCustomizeProductHeaderDropdownsDisabled ||
    pendingCloseOperation

  const disableCustomize = Boolean(
    isLoading ||
      generating ||
      pendingCloseOperation ||
      !canCustomize ||
      showingFinalizingText ||
      showingRevertFinalizationText,
  )

  const denyUserEntryIntoPdfViewer = Boolean(
    showingRevertFinalizationText || regularUserShouldNotHaveAccessToFinalPdf,
  )

  const goToV1 = () => {
    const V1 = `https://directorsprintsuite.com/order/${order.id}/${order.case_id}`
    window.open(V1, '_blank')
  }

  return (
    <>
      <Flex column>
        <Flex
          margin={'0 0 32px'}
          padding="0 40px"
          justify="space-between"
          align="center"
        >
          <Flex column>
            <Text size="24px" weight="600">
              {t(getItemDisplayName(productInfo?.name, 'products', t))}
              {productInfo?.nickname ? ` (${productInfo.nickname})` : ''}
            </Text>
            <Text>{t(getItemDisplayName(themeInfo?.name, 'themes', t))}</Text>
          </Flex>
          <Flex>
            <DesignerGate>
              <Dropdown
                selected={[layout, setLayout]}
                placeholder={t('customizeProductHeader.layout')}
                textKey="name"
                valueKey="id"
                label={t('customizeProductHeader.layout')}
                items={allLayouts}
                margin="0 16px 0 0"
                disabled={disableDropdownButtons}
              />
            </DesignerGate>
            <DesignerGate>
              <Dropdown
                selected={[productStatus, handleDropdownProductStatusChange]}
                placeholder={t('customizeProductHeader.status')}
                textKey="name"
                valueKey="id"
                label={t('customizeProductHeader.statusLabel')}
                items={productStatusValues}
                margin="0 16px 0 0"
                disabled={disableDropdownButtons}
              />
            </DesignerGate>
            {!oldProduct && (
              <Flex gap="16px">
                {!canRevertFinalization && (
                  <>
                    <Flex disabled={disableCustomize}>
                      <Link
                        disabled={disableCustomize}
                        href={buildUrlWithParams(
                          `/editor/${pageSet?.id}`,
                          params,
                        )}
                      >
                        <Button
                          data-testid="customize-button"
                          margin="19px 0 0 0"
                          disabled={disableCustomize}
                          onClick={handleCustomizeClick}
                        >
                          {applicationType === M3
                            ? t('customizeProductHeader.customizeM3')
                            : t('customizeProductHeader.customize')}
                        </Button>
                      </Link>
                    </Flex>
                    {!isProductStatusClosed && (
                      <Button
                        data-testid="generate-proof-button"
                        margin="19px 0 0 0"
                        onClick={handleGeneratePdfClick}
                        disabled={denyUserEntryIntoPdfViewer}
                        primary
                      >
                        {generatingText}
                      </Button>
                    )}
                  </>
                )}
                {canRevertFinalization && (
                  <Button
                    margin="19px 0 0 0"
                    onClick={handleRevertFinalizationClick}
                  >
                    {t('customizeProductHeader.revertFinalization')}
                  </Button>
                )}
              </Flex>
            )}
            {oldProduct && (
              <Button primary margin="19px 16px 0 0" onClick={() => goToV1()}>
                {t('customizeProductHeader.openOnV1')}
              </Button>
            )}
          </Flex>
        </Flex>
        <PrintGuideModal
          show={[showPrintGuide, setShowPrintGuide]}
          selectedLayout={selectedLayout}
          productInfo={productInfo}
        />
      </Flex>

      <PdfViewer
        canCustomize={canCustomize}
        customizeUrl={customizeUrl}
        disclaimerAcknowledged={disclaimerAcknowledged}
        isProof={isProof}
        handleClosePdfViewer={handleClosePdfViewer}
        handleFinalizePdf={handleFinalizePdf}
        pdfGenerationProgress={pdfGenerationProgress}
        pdfGenerationStatus={pdfGenerationStatus}
        pdfUrl={pdfUrl}
        productStatus={productStatus}
        showPdfViewer={showPdfViewer}
        setDisclaimerAcknowledged={setDisclaimerAcknowledged}
        setProductStatus={setProductStatus}
        applicationType={applicationType}
        proofGalleryDetails={proofGalleryDetails}
      />
    </>
  )
}

export default CustomizeProductHeader
